import { Diagnostic, Geotab } from "@fairfleet/geotab";
import { useQuery } from "@tanstack/react-query";
import {
  CHECK_DIAGNOSTIC_IDS,
  CHECK_DIAGNOSTIC_TYPES,
  isCheckDiagnostic,
} from "../utilities/checkDiagnostics";
import { useGeotabClient } from "@/components/GeotabClientContext";
import { RESULTS_LIMIT_DIAGNOSTIC_TYPE_SEARCH } from "@/constants/resultsLimits";

/** The catalog changes when a database adds a custom diagnostic, which is rare. */
const STALE_TIME_MS = 60 * 60 * 1000;

export interface CheckDiagnostics {
  /** Sorted, because the scan and the lookups key on it. */
  ids: string[];
  /** The read came back full, so a diagnostic the check asks about may be missing. */
  isTruncated: boolean;
}

/**
 * The diagnostics of this database the checklist asks about.
 *
 * The scan reads one series per diagnostic for the whole fleet, so it has to know them up front.
 * The fixed ids whether or not the read found them, and from one `Get` of the catalog the CAN
 * protocols and add-on devices, which have no single id.
 */
export async function fetchCheckDiagnostics(
  geotabClient: Geotab,
  signal?: AbortSignal,
): Promise<CheckDiagnostics> {
  const diagnostics = ((await geotabClient.get(
    "Diagnostic",
    { diagnosticTypes: [...CHECK_DIAGNOSTIC_TYPES] },
    RESULTS_LIMIT_DIAGNOSTIC_TYPE_SEARCH,
    signal,
  )) ?? []) as Diagnostic[];

  const ids = new Set(CHECK_DIAGNOSTIC_IDS);
  for (const diagnostic of diagnostics) {
    if (isCheckDiagnostic(diagnostic)) {
      ids.add(diagnostic.id);
    }
  }

  return {
    ids: [...ids].sort(),
    isTruncated: diagnostics.length >= RESULTS_LIMIT_DIAGNOSTIC_TYPE_SEARCH,
  };
}

export function useCheckDiagnosticIds(enabled = true) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  return useQuery({
    queryKey: ["vehicleDataCheckDiagnostics", activeSessionId],
    queryFn: ({ signal }) => fetchCheckDiagnostics(geotabClient, signal),
    enabled,
    staleTime: STALE_TIME_MS,
    refetchOnWindowFocus: false,
  });
}
